import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import {
  useEffect,
  useState,
} from 'react';
import { Button } from 'react-aria-components';
import { BsFillGridFill } from 'react-icons/bs';
import { FaList } from 'react-icons/fa6';
import { useIsMounted } from 'usehooks-ts';

import { listingsViewAtom } from '@/features/listing/atoms/listings-view-atom';
import ListingFilterNew from '@/features/listing/components/listings-filters/listing-filter-new/listing-filter-new';
import { LISTING_VIEW_VARIANTS } from '@/features/listing/constants/listing-constants';
import {
  getCookieListingView,
  setCookieListingView,
} from '@/features/listing/cookies/cookie-listing-view';
import { type ListingsFilterName } from '@/features/listing/types/listing-filters-types';

import styles from './listing-view-select.module.scss';

interface ListingViewSelectProps {
  onFilterDisplayChange: (filterName: ListingsFilterName | null) => void;
}

export default function ListingViewSelect(props: ListingViewSelectProps) {
  const { onFilterDisplayChange } = props;

  const isMounted = useIsMounted();
  const filtersViewTranslate = useTranslations('listing.filters.view');

  const [listingsView,
    setListingView] = useAtom(listingsViewAtom);

  const [isPopoverOpen,
    setPopoverOpen] = useState(false);

  const handleViewChange = (value: string) => () => {
    const valueToSet = value === LISTING_VIEW_VARIANTS.list
      ? LISTING_VIEW_VARIANTS.list
      : LISTING_VIEW_VARIANTS.grid;

    setListingView(valueToSet);
    setCookieListingView(valueToSet);
    onFilterDisplayChange(null);
    setPopoverOpen(false);
  };

  const renderButtonText = () => (
    <>
      {listingsView === LISTING_VIEW_VARIANTS.list ? (
        // eslint-disable-next-line canonical/prefer-react-lazy
        <FaList className={styles.listingViewSelect_dropdownIcon} />
      ) : (
        // eslint-disable-next-line canonical/prefer-react-lazy
        <BsFillGridFill className={styles.listingViewSelect_dropdownIcon} />
      )}
      {filtersViewTranslate(`options.${listingsView}`)}
    </>
  );

  useEffect(() => {
    const cookieListingView = getCookieListingView();

    if (cookieListingView) {
      setListingView(cookieListingView);
    }
  }, [isMounted, setListingView]);

  return (
    <ListingFilterNew
      buttonText={renderButtonText()}
      dialogTitle={filtersViewTranslate('dropdown-title')}
      onFilterDisplayChange={onFilterDisplayChange}
      listingFilterName="listingsView"
      isPopoverOpen={isPopoverOpen}
      setPopoverOpen={setPopoverOpen}
    >
      <div
        className={styles.listingViewSelect_options}
      >
        <Button
          className={styles.listingViewSelect_optionItem}
          value={LISTING_VIEW_VARIANTS.list}
          isDisabled={listingsView === LISTING_VIEW_VARIANTS.list}
          onPress={handleViewChange(LISTING_VIEW_VARIANTS.list)}
        >
          <FaList className={styles.listingViewSelect_optionIcon} />

          <span>
            {filtersViewTranslate('options.list')}
          </span>
        </Button>

        <Button
          className={styles.listingViewSelect_optionItem}
          isDisabled={listingsView === LISTING_VIEW_VARIANTS.grid}
          value={LISTING_VIEW_VARIANTS.grid}
          onPress={handleViewChange(LISTING_VIEW_VARIANTS.grid)}
        >
          <div className={styles.listingViewSelect_optionItem}>
            <BsFillGridFill className={styles.listingViewSelect_optionIcon} />

            <span>
              {filtersViewTranslate('options.grid')}
            </span>
          </div>

        </Button>
      </div>
    </ListingFilterNew>
  );
}
